/**
 * Vincent B923 + B924 (Eric 2026-02-14 SPEC STRICK PROD) — Gestion
 * manuelle + auto des étiquettes sans surcharger l'interface.
 *
 * B923 : la chip « ÉTIQUETTES » sous le titre devient cliquable. Au
 *        clic, un dialog s'ouvre permettant :
 *        (a) d'ajouter manuellement un tag (Enter valide, comma sépare)
 *        (b) de déclencher la suggestion auto (POST /tags/suggest)
 *        (c) de sélectionner/rejeter chaque suggestion (accept/reject)
 *        (d) de supprimer un tag existant
 *        L'interface reste épurée : aucun bouton visible hors du dialog.
 * B924 : le tooltip natif `title=` affiche déjà tous les tags (pas
 *        seulement 3+N) — voir VideoDetailHeader chip video-meta-tags.
 *        On garde ce comportement HTML natif car il fonctionne
 *        parfaitement pour du survol (aucune API JS requise).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkle, Plus, Check, XCircle, Trash } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export const TagsManagerDialog = ({
  open,
  onOpenChange,
  videoId,
  currentTags = [],
  onTagsChanged,
  lang = "fr",
  canEdit = true,
}) => {
  const [tags, setTags] = useState(currentTags);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Vincent B925 (Eric 2026-02-14 SPEC STRICK PROD) — Vocabulaire org
  // pour l'auto-complétion de l'input manuel. Chargé une fois à
  // l'ouverture du dialog. Trié par popularité DESC côté backend.
  const [orgVocab, setOrgVocab] = useState([]); // [{tag, count}]
  const [autocompleteIndex, setAutocompleteIndex] = useState(-1);
  // Vincent B927 (Eric 2026-02-14 SPEC STRICK PROD) — Fusions suggérées
  // sur les paires quasi-identiques (« retraite/retraiter »,
  // « assurance vie/assurance-vie »). Chargées à l'ouverture, cap 100.
  const [mergeNudges, setMergeNudges] = useState([]);
  // Vincent B962 (Eric 2026-02-14 SPEC STRICK PROD) — Choix user du
  // canonical par nudge (key = `${canonical}::${variant}`). Vide = user
  // n'a pas touche = defaut backend applique.
  const [nudgeCustomCanonical, setNudgeCustomCanonical] = useState({});

  useEffect(() => {
    setTags(currentTags);
  }, [currentTags]);

  // Charge les suggestions existantes à l'ouverture (sans forcer un re-run
  // du LLM). Le bouton ✨ permet de déclencher un re-run manuel.
  useEffect(() => {
    if (!open || !videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/videos/${videoId}/tags/suggestions`,
          { withCredentials: true },
        );
        if (!cancelled) setSuggestions(data?.suggested_tags || []);
      } catch {
        // silencieux — pas de suggestions = état vide, pas une erreur
      }
    })();
    return () => { cancelled = true; };
  }, [open, videoId]);

  // Vincent B925 (Eric 2026-02-14 SPEC STRICK PROD) — Charge le
  // vocabulaire tags de l'org une fois à l'ouverture pour alimenter
  // l'auto-complétion. Endpoint peu coûteux (agrégation Mongo cache-friendly).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/tags/org-vocabulary`,
          { withCredentials: true },
        );
        if (!cancelled) setOrgVocab(data?.tags || []);
      } catch {
        // silencieux — sans vocab, l'input reste fonctionnel sans autocomplete
      }
      try {
        // Vincent B927 — Charge en parallèle les fusions suggérées.
        const { data: nd } = await axios.get(
          `${API}/api/tags/merge-nudges`,
          { withCredentials: true },
        );
        if (!cancelled) setMergeNudges(nd?.nudges || []);
      } catch {
        // silencieux — pas de nudges = section masquée
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Vincent B927 — Applique une fusion (canonical ⇐ variant) en un
  // clic. Rafraîchit ensuite le vocabulaire ET la liste des nudges.
  const handleApplyMergeNudge = async (canonical, variant) => {
    try {
      const { data } = await axios.post(
        `${API}/api/tags/apply-merge-nudge`,
        { canonical, variant },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `Fusion appliquée : ${data.pulled} vidéo(s) migrée(s) de « ${variant} » vers « ${canonical} ».`
        : `Merge applied: ${data.pulled} video(s) migrated from "${variant}" to "${canonical}".`);
      // Refresh vocab + nudges
      setMergeNudges((cur) => cur.filter((n) => !(n.canonical === canonical && n.variant === variant)));
      try {
        const { data: v } = await axios.get(`${API}/api/tags/org-vocabulary`, { withCredentials: true });
        setOrgVocab(v?.tags || []);
      } catch {}
      // Si le document actuel avait le variant, le remplacer par canonical.
      if ((tags || []).some((t) => String(t).toLowerCase() === variant.toLowerCase())) {
        const next = tags
          .filter((t) => String(t).toLowerCase() !== variant.toLowerCase());
        if (!next.some((t) => String(t).toLowerCase() === canonical.toLowerCase())) {
          next.push(canonical);
        }
        await _saveTags(next);
      }
    } catch (e) {
      toast.error((lang === "fr" ? "Fusion échouée : " : "Merge failed: ") + (e?.response?.data?.detail || e?.message));
    }
  };

  // Vincent B925 — Filtre le vocabulaire pour l'auto-complétion : match
  // case-insensitive sur le préfixe OU sur une sous-chaîne, exclut les
  // tags déjà appliqués, cap à 8 résultats visibles.
  const autocompleteMatches = (() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    const applied = new Set((tags || []).map((t) => String(t).toLowerCase()));
    const out = [];
    for (const item of orgVocab) {
      const tag = String(item.tag || "");
      const low = tag.toLowerCase();
      if (applied.has(low)) continue;
      if (!low.includes(q)) continue;
      out.push(item);
      if (out.length >= 8) break;
    }
    return out;
  })();

  const _saveTags = useCallback(async (newTags) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/videos/${videoId}/metadata`,
        { tags: newTags },
        { withCredentials: true },
      );
      setTags(newTags);
      onTagsChanged?.(newTags);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || (lang === "fr" ? "erreur inconnue" : "unknown error");
      toast.error((lang === "fr" ? "Échec de la sauvegarde : " : "Save failed: ") + detail);
    }
    setSaving(false);
  }, [videoId, onTagsChanged, lang]);

  const handleAddManual = async () => {
    const raw = input.trim();
    if (!raw) return;
    // Support « virgule sépare plusieurs tags »
    const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
    const merged = [...tags];
    let added = 0;
    for (const p of parts) {
      if (!merged.some((t) => t.toLowerCase() === p.toLowerCase())) {
        merged.push(p.slice(0, 50));
        added++;
      }
    }
    if (added === 0) {
      toast.info(lang === "fr" ? "Ce tag existe déjà." : "Tag already exists.");
      return;
    }
    setInput("");
    setAutocompleteIndex(-1);
    await _saveTags(merged);
    toast.success(lang === "fr" ? `${added} tag(s) ajouté(s).` : `${added} tag(s) added.`);
  };

  // Vincent B925 — Ajoute un tag depuis l'auto-complétion (clic ou Enter
  // sur une entrée sélectionnée). Reprend la logique dédup + save + toast.
  const handleAddFromAutocomplete = async (tag) => {
    const merged = [...tags];
    if (merged.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      toast.info(lang === "fr" ? "Ce tag existe déjà." : "Tag already exists.");
      return;
    }
    merged.push(String(tag).slice(0, 50));
    setInput("");
    setAutocompleteIndex(-1);
    await _saveTags(merged);
    toast.success(lang === "fr" ? "Tag ajouté depuis le vocabulaire de l'organisation." : "Tag added from org vocabulary.");
  };

  const handleRemoveTag = async (tag) => {
    const next = tags.filter((t) => t !== tag);
    await _saveTags(next);
  };

  const handleRunSuggest = async () => {
    setSuggestLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/tags/suggest`,
        {},
        { withCredentials: true },
      );
      setSuggestions(data?.suggested_tags || []);
      const n = (data?.suggested_tags || []).length;
      if (n > 0) {
        toast.success(lang === "fr"
          ? `${n} suggestion(s) générée(s).`
          : `${n} suggestion(s) generated.`);
      } else {
        toast.info(lang === "fr"
          ? "Aucune suggestion trouvée (contenu insuffisant)."
          : "No suggestion found (not enough content).");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "?";
      toast.error((lang === "fr" ? "Échec des suggestions : " : "Suggest failed: ") + detail);
    }
    setSuggestLoading(false);
  };

  const handleAcceptSuggestion = async (tag) => {
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/tags/accept`,
        { tags: [tag] },
        { withCredentials: true },
      );
      setTags(data?.tags || []);
      setSuggestions(data?.suggested_tags || []);
      onTagsChanged?.(data?.tags || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Refus d'accepter : " : "Accept failed: ") + (e?.response?.data?.detail || e?.message));
    }
  };

  const handleRejectSuggestion = async (tag) => {
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/tags/reject`,
        { tags: [tag] },
        { withCredentials: true },
      );
      setSuggestions(data?.suggested_tags || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Refus : " : "Reject failed: ") + (e?.response?.data?.detail || e?.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tags-manager-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "fr" ? "Étiquettes du document" : "Document tags"}
          </DialogTitle>
          <DialogDescription>
            {lang === "fr"
              ? "Ajoute manuellement des tags ou active la suggestion automatique."
              : "Add tags manually or run automatic suggestions."}
          </DialogDescription>
        </DialogHeader>

        {/* Bloc 1 — Tags actuels (chips supprimables) */}
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
            {lang === "fr" ? "Actuels" : "Current"} · <span data-testid="tags-manager-count">{tags.length}</span>
          </div>
          {tags.length === 0 ? (
            <div className="text-xs text-zinc-400 italic" data-testid="tags-manager-empty">
              {lang === "fr" ? "Aucun tag pour le moment." : "No tags yet."}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="tags-manager-current-list">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-teal-50 border border-teal-300 text-xs text-teal-800"
                  data-testid={`tags-manager-chip-${t}`}
                >
                  {t}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(t)}
                      className="w-4 h-4 rounded-full hover:bg-red-100 flex items-center justify-center text-teal-600 hover:text-red-600"
                      data-testid={`tags-manager-remove-${t}`}
                      title={lang === "fr" ? `Retirer ${t}` : `Remove ${t}`}
                      disabled={saving}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Bloc 2 — Ajout manuel */}
        {canEdit && (
          <div className="mt-4 border-t border-zinc-200 pt-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              {lang === "fr" ? "Ajouter manuellement" : "Add manually"}
            </div>
            <div className="flex gap-1 relative">
              <Input
                value={input}
                onChange={(e) => { setInput(e.target.value); setAutocompleteIndex(-1); }}
                onKeyDown={(e) => {
                  // Vincent B925 (Eric 2026-02-14 SPEC STRICK PROD) —
                  // Navigation clavier dans l'auto-complétion : flèches
                  // haut/bas pour sélectionner, Enter pour valider
                  // (autocomplete OU ajout manuel selon focus).
                  if (autocompleteMatches.length > 0) {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setAutocompleteIndex((i) => Math.min(autocompleteMatches.length - 1, i + 1));
                      return;
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setAutocompleteIndex((i) => Math.max(-1, i - 1));
                      return;
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setAutocompleteIndex(-1);
                      return;
                    }
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (autocompleteIndex >= 0 && autocompleteMatches[autocompleteIndex]) {
                      handleAddFromAutocomplete(autocompleteMatches[autocompleteIndex].tag);
                    } else {
                      handleAddManual();
                    }
                  }
                }}
                placeholder={lang === "fr"
                  ? "ex : REEE, planification retraite (virgule pour séparer)"
                  : "e.g. RESP, retirement planning (comma to separate)"}
                className="h-8 text-sm"
                data-testid="tags-manager-manual-input"
                disabled={saving}
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls="tags-manager-autocomplete-list"
              />
              <Button
                type="button"
                onClick={handleAddManual}
                disabled={!input.trim() || saving}
                className="h-8 px-3"
                data-testid="tags-manager-add-btn"
              >
                <Plus size={14} weight="bold" />
                <span className="ml-1">{lang === "fr" ? "Ajouter" : "Add"}</span>
              </Button>

              {/* Vincent B925 (Eric 2026-02-14 SPEC STRICK PROD) —
                  Dropdown auto-complétion. Positionné en absolute sous
                  l'input, montre les tags existants dans l'org qui
                  matchent la saisie. Trié par popularité DESC (backend).
                  Empty state silencieux — l'input reste utilisable
                  pour créer un nouveau tag. */}
              {autocompleteMatches.length > 0 && (
                <ul
                  id="tags-manager-autocomplete-list"
                  className="absolute left-0 right-16 top-full mt-1 max-h-56 overflow-y-auto rounded border border-zinc-300 bg-white shadow-lg z-50"
                  data-testid="tags-manager-autocomplete-list"
                  role="listbox"
                >
                  {autocompleteMatches.map((m, i) => (
                    <li
                      key={m.tag}
                      role="option"
                      aria-selected={i === autocompleteIndex}
                      onMouseDown={(e) => { e.preventDefault(); handleAddFromAutocomplete(m.tag); }}
                      onMouseEnter={() => setAutocompleteIndex(i)}
                      className={`px-2 py-1 text-xs cursor-pointer flex items-center justify-between ${
                        i === autocompleteIndex ? "bg-teal-50 text-teal-800" : "hover:bg-zinc-50"
                      }`}
                      data-testid={`tags-manager-autocomplete-item-${m.tag}`}
                    >
                      <span className="truncate">{m.tag}</span>
                      <span className="ml-2 text-[10px] text-zinc-400 tabular-nums" data-testid={`tags-manager-autocomplete-count-${m.tag}`}>
                        {m.count}×
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">
              {lang === "fr"
                ? "Astuce : ↑↓ pour naviguer dans l'auto-complétion, Entrée pour valider, virgule pour séparer plusieurs tags."
                : "Tip: ↑↓ to navigate autocomplete, Enter to submit, comma to separate multiple tags."}
            </p>
          </div>
        )}

        {/* Bloc 3 — Suggestions auto */}
        {canEdit && (
          <div className="mt-4 border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                {lang === "fr" ? "Suggestions auto" : "Auto suggestions"}
                {suggestions.length > 0 && (
                  <span className="ml-1 text-zinc-400" data-testid="tags-manager-suggestions-count">
                    · {suggestions.length}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleRunSuggest}
                disabled={suggestLoading}
                className="h-7 px-2 text-xs"
                data-testid="tags-manager-suggest-btn"
              >
                <Sparkle size={12} weight="fill" className={suggestLoading ? "animate-pulse" : ""} />
                <span className="ml-1">
                  {suggestLoading
                    ? (lang === "fr" ? "En cours…" : "Running…")
                    : (lang === "fr" ? "Générer" : "Generate")}
                </span>
              </Button>
            </div>
            {suggestions.length === 0 && !suggestLoading ? (
              <div className="text-xs text-zinc-400 italic" data-testid="tags-manager-no-suggestions">
                {lang === "fr"
                  ? "Aucune suggestion pour l'instant — clique sur « Générer »."
                  : "No suggestion yet — click \"Generate\"."}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="tags-manager-suggestions-list">
                {suggestions.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-violet-50 border border-violet-300 text-xs text-violet-800"
                    data-testid={`tags-manager-suggestion-${s}`}
                  >
                    {s}
                    <button
                      type="button"
                      onClick={() => handleAcceptSuggestion(s)}
                      className="w-4 h-4 rounded-full hover:bg-emerald-100 flex items-center justify-center text-emerald-600"
                      data-testid={`tags-manager-accept-${s}`}
                      title={lang === "fr" ? `Accepter ${s}` : `Accept ${s}`}
                    >
                      <Check size={10} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRejectSuggestion(s)}
                      className="w-4 h-4 rounded-full hover:bg-red-100 flex items-center justify-center text-red-600"
                      data-testid={`tags-manager-reject-${s}`}
                      title={lang === "fr" ? `Rejeter ${s}` : `Reject ${s}`}
                    >
                      <XCircle size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vincent B927 (Eric 2026-02-14 SPEC STRICK PROD) — Section
            « Fusions suggérées » : détecte auto les paires quasi-identiques
            dans le vocabulaire org (« retraite/retraiter »,
            « assurance vie/assurance-vie »). Ne s'affiche que si le backend
            trouve au moins 1 candidat, pour éviter le bruit. Un clic
            applique la fusion sur toute l'org (idempotent). */}
        {canEdit && mergeNudges.length > 0 && (
          <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="tags-manager-merge-nudges">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              {lang === "fr" ? "🔀 Fusions suggérées" : "🔀 Suggested merges"}
              <span className="ml-1 text-zinc-400" data-testid="tags-manager-nudges-count">
                · {mergeNudges.length}
              </span>
            </div>
            <p className="text-[10px] text-zinc-500 mb-1.5">
              {lang === "fr"
                ? "Paires quasi-identiques dans l'organisation. Cliquer pour fusionner sur toute l'org."
                : "Near-duplicate pairs in the org. Click to merge across the whole org."}
            </p>
            <ul className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="tags-manager-nudges-list">
              {mergeNudges.slice(0, 20).map((n) => {
                // Vincent B962 (Eric 2026-02-14 SPEC STRICK PROD) — Le user
                // peut choisir la variante de reference (radio) ou en taper
                // une custom. Le backend expose maintenant `alternatives`
                // avec un score par variante — on l'utilise pour proposer
                // le meilleur choix en pre-selection.
                const nudgeKey = `${n.canonical}::${n.variant}`;
                const alternatives = (n.alternatives && n.alternatives.length > 0)
                  ? n.alternatives
                  : [
                      { tag: n.canonical, count: n.canonical_count, score: 0 },
                      { tag: n.variant, count: n.variant_count, score: 0 },
                    ];
                const chosen = nudgeCustomCanonical[nudgeKey] ?? n.canonical;
                const isCustom = !alternatives.some((a) => a.tag === chosen);
                return (
                <li
                  key={nudgeKey}
                  className="text-xs bg-rose-50 border border-rose-200 rounded px-2 py-2"
                  data-testid={`tags-manager-nudge-${n.canonical}-${n.variant}`}
                  data-reason={n.reason}
                  data-similarity={n.similarity}
                >
                  <div className="text-[10px] text-zinc-500 mb-1">
                    {lang === "fr" ? "Choisir la version à conserver :" : "Choose the version to keep:"}
                    <span className="ml-1 text-zinc-400">
                      · {n.reason === "variante_ponctuation"
                        ? (lang === "fr" ? "variante ponctuation/casse/accent" : "punctuation/case/accent variant")
                        : `~${Math.round(n.similarity * 100)}%`}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mb-1">
                    {alternatives.map((alt) => (
                      <label
                        key={alt.tag}
                        className={`flex items-center gap-2 text-xs cursor-pointer ${
                          chosen === alt.tag ? "font-semibold text-teal-800" : "text-zinc-700"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`nudge-canonical-${nudgeKey}`}
                          checked={chosen === alt.tag}
                          onChange={() => setNudgeCustomCanonical((p) => ({ ...p, [nudgeKey]: alt.tag }))}
                          data-testid={`nudge-canonical-radio-${nudgeKey}-${alt.tag}`}
                        />
                        <span>{alt.tag}</span>
                        <span className="text-[10px] text-zinc-400">({alt.count}×)</span>
                        {typeof alt.score === "number" && alt.score > 0 && (
                          <span className="text-[9px] text-zinc-400" title="Score heuristique B962">
                            · score {alt.score}
                          </span>
                        )}
                      </label>
                    ))}
                    <label className={`flex items-center gap-2 text-xs cursor-pointer ${
                      isCustom ? "font-semibold text-teal-800" : "text-zinc-700"
                    }`}>
                      <input
                        type="radio"
                        name={`nudge-canonical-${nudgeKey}`}
                        checked={isCustom}
                        onChange={() => setNudgeCustomCanonical((p) => ({ ...p, [nudgeKey]: "" }))}
                        data-testid={`nudge-canonical-radio-${nudgeKey}-custom`}
                      />
                      <input
                        type="text"
                        placeholder={lang === "fr" ? "Version personnalisée…" : "Custom version…"}
                        value={isCustom ? chosen : ""}
                        onChange={(e) => setNudgeCustomCanonical((p) => ({ ...p, [nudgeKey]: e.target.value }))}
                        onFocus={() => setNudgeCustomCanonical((p) => (isCustom ? p : { ...p, [nudgeKey]: "" }))}
                        className="text-xs px-1 py-0.5 border border-zinc-300 rounded flex-1 min-w-[120px]"
                        data-testid={`nudge-canonical-custom-input-${nudgeKey}`}
                      />
                    </label>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const finalCanonical = String(chosen || "").trim();
                        if (!finalCanonical) {
                          toast.error(lang === "fr" ? "Choisis ou tape une version." : "Choose or type a version.");
                          return;
                        }
                        // Fusionne toutes les autres variantes vers finalCanonical.
                        const variantsToMerge = alternatives.map((a) => a.tag).filter((t) => t !== finalCanonical);
                        (async () => {
                          for (const v of variantsToMerge) {
                            await handleApplyMergeNudge(finalCanonical, v);
                          }
                        })();
                      }}
                      className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700"
                      data-testid={`tags-manager-nudge-apply-${n.canonical}-${n.variant}`}
                      title={lang === "fr"
                        ? `Fusionner les autres variantes vers « ${chosen} »`
                        : `Merge other variants into "${chosen}"`}
                    >
                      {lang === "fr" ? "Fusionner" : "Merge"}
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="tags-manager-close-btn"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TagsManagerDialog;
